import React from 'react';
import { SwitchPeak, SwitchSummary } from 'api/services/capacityLimits.service';

export const CHANNELS_COPY =
  'Channels = total concurrent calls (inbound + outbound); at the cap new outbound calls are refused with 503, inbound is counted only.';

/** A trunk counted in both directions: inbound + outbound current, and the sum of the two peaks. */
export interface TrunkTotal { dimensionValue: string; current: number; peak: number }

/**
 * Per-trunk totals where the switch reports the trunk in BOTH directions. The two peaks
 * may have happened at different moments, so their sum is an upper bound on the true
 * combined peak, and is labelled as such.
 */
export const trunkTotals = (peaks: SwitchPeak[]): TrunkTotal[] => {
  const by: Record<string, { in?: SwitchPeak; out?: SwitchPeak }> = {};
  peaks.filter(p => p.dimensionType === 'trunk').forEach(p => {
    const e = (by[p.dimensionValue] = by[p.dimensionValue] || {});
    if (p.direction === 'inbound') e.in = p; else e.out = p;
  });
  return Object.entries(by)
    .filter(([, e]) => e.in && e.out)
    .map(([v, e]) => ({ dimensionValue: v, current: e.in!.current + e.out!.current, peak: e.in!.peak + e.out!.peak }))
    .sort((a, b) => a.dimensionValue.localeCompare(b.dimensionValue));
};

const TH = 'px-2 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider';
const TD = 'px-2 py-1.5 text-xs text-gray-700 dark:text-gray-300 align-top';
const when = (v: string | null | undefined) => (v ? new Date(v).toLocaleString() : '—');
const num = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v);

export const modeLabel = (mode: 'shadow' | 'enforce' | null | undefined): { text: string; tone: string } => {
  if (mode === 'enforce') return { text: 'enforce', tone: 'bg-red-100 text-red-800' };
  if (mode === 'shadow') return { text: 'shadow (log only)', tone: 'bg-amber-100 text-amber-800' };
  return { text: 'unknown', tone: 'bg-gray-200 text-gray-700' };
};

const yesNo = (v: boolean | null | undefined) => (v === true ? 'yes' : v === false ? 'no' : 'unknown');

/**
 * What each switch says about its own enforcement: shadow vs enforce, whether its
 * concurrency counters are safe (cpass config vs what the switch reports), the reconciler,
 * refusals, and the live/peak concurrency it has counted (outbound and inbound).
 */
const SwitchRuntimeSection: React.FC<{ switches: SwitchSummary[] }> = ({ switches }) => (
  <div>
    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Switch-side enforcement</h3>
    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2" data-testid="channels-copy">{CHANNELS_COPY}</p>
    {switches.length === 0 && <p className="text-xs text-gray-500">No switches.</p>}
    <div className="space-y-3">
      {switches.map(s => {
        const rt = s.runtime;
        const m = modeLabel(rt?.reachable ? rt.mode : null);
        const disagree = rt?.reachable && rt.concurrencySafeReported !== null && rt.concurrencySafeReported !== undefined
          && !!s.concurrencySafe !== rt.concurrencySafeReported;
        return (
          <div key={s.providerId} data-testid={`runtime-${s.providerId}`} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-semibold text-gray-900 dark:text-white">{s.name}</span>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${m.tone}`} data-testid="mode-badge">{m.text}</span>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                concurrency-safe: cpass {s.concurrencySafe ? 'yes' : 'no'} / switch {rt?.reachable ? yesNo(rt.concurrencySafeReported) : 'unknown'}
              </span>
              {disagree && <span className="text-xs font-medium text-red-700">cpass and the switch disagree</span>}
              {!s.concurrencySafe && <span className="text-xs text-gray-500">(only CPS can be pushed here)</span>}
            </div>
            {!rt && <p className="text-xs text-gray-500 mt-1" data-testid="runtime-none">Not reachable / not read (no control URL or admin token).</p>}
            {rt && !rt.reachable && (
              <p className="text-xs text-red-700 mt-1" data-testid="runtime-error">
                Not reachable{rt.error ? `: ${rt.error}` : ''}
                {rt.error === 'patch not applied' && ' — the switch has no enforcement endpoint yet (2026-10-14 capacity patch).'}
              </p>
            )}
            {rt && rt.reachable && (
              <>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 flex flex-wrap gap-x-4">
                  {rt.patch && <span>patch {rt.patch}</span>}
                  <span>reconciler last run {when(rt.reconcilerLastRunAt)}</span>
                  <span>Asterisk channels {num(rt.asteriskChannels)}</span>
                  <span>would refuse {num(rt.wouldRefuseTotal)}</span>
                  <span>refused {num(rt.refusedTotal)}</span>
                  {rt.error && <span className="text-amber-700">{rt.error}</span>}
                </div>
                {rt.peaks.length === 0 ? <p className="text-xs text-gray-500 mt-1">No counters yet.</p> : (
                  <div className="overflow-x-auto mt-2">
                    <table className="w-full" data-testid="peaks-table">
                      <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr>{['Direction', 'Key', 'Now', 'Peak', 'Peak at'].map(h => <th key={h} className={TH}>{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {[...rt.peaks].sort((a, b) => a.direction.localeCompare(b.direction) || b.peak - a.peak).map(p => (
                          <tr key={`${p.direction}-${p.dimensionType}-${p.dimensionValue}`}>
                            <td className={TD}>{p.direction}</td>
                            <td className={`${TD} font-mono`}>{p.dimensionType}={p.dimensionValue}</td>
                            <td className={TD}>{p.current}</td>
                            <td className={TD}>{p.peak}</td>
                            <td className={TD}>{when(p.peakAt)}</td>
                          </tr>
                        ))}
                        {trunkTotals(rt.peaks).map(t => (
                          <tr key={`total-${t.dimensionValue}`} data-testid="trunk-total" className="font-medium">
                            <td className={TD}>total (in + out)</td>
                            <td className={`${TD} font-mono`}>trunk={t.dimensionValue}</td>
                            <td className={TD}>{t.current}</td>
                            <td className={TD} title="Sum of the inbound and outbound peaks; they may not have coincided, so this is an upper bound">≤ {t.peak}</td>
                            <td className={TD}>—</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {rt.peakSince && <p className="text-xs text-gray-400 mt-1">Peaks since {when(rt.peakSince)}. Inbound is counted only (shadow), never refused.</p>}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

export default SwitchRuntimeSection;
